import { Injectable } from '@angular/core';
import {
  GcSuccessPageTextMap,
  GiftCardSettingService,
} from '@features/gift-card/services/gift-card-setting/gift-card-setting.service';
import { GiftCardHelper } from '@rd/helpers/gift-card.helper';
import {
  CashPointsRatio,
  CheckoutTermsListItem,
  CurrencyFormat,
  GiftCard,
  GiftCardDenomination,
  GiftCardOrderItemData,
  Order,
  OrderItemAttributes,
  OrderItemType,
  SuccessButton,
  SuccessButtonGroup,
} from '@rd/types';

export enum WestpacGiftCardCategory {
  All = 'all',
  Retail = 'retail',
  Dining = 'dining',
  BeautyAndHealth = 'beauty',
  Others = 'others',
}

@Injectable()
export class WestpacGiftCardSettingService extends GiftCardSettingService {
  cashPointsRatio = CashPointsRatio.FullCash;
  giftCardCategoryOptions = Object.values(WestpacGiftCardCategory);

  shouldRequireAgreeTnc = true;
  /*
  ====================================
    Listing page-only settings
  ====================================
  */

  listingPageLowestDenominationCurrencyFormat: CurrencyFormat = 'codeWithSpace';
  listingPageHighestDenominationCurrencyFormat: CurrencyFormat = '';
  shouldRenderListingPageBreadcrumb = true;

  /*
  ====================================
    Success page-only settings
  ====================================
  */

  successPageCurrencyFormat = 'codeWithSpace';

  successPageTextOverrides: Partial<GcSuccessPageTextMap> = {
    contactUsMessage: null,
    manageGiftCardMessage: {
      translationKey: 'gift_card.success.manage_gift_card',
    },
  };

  constructor(protected giftCardHelper: GiftCardHelper) {
    super(giftCardHelper);
  }

  /*
  ====================================
    Checkout/details page-only settings
  ====================================
  */

  shouldHaveArrowOnGcTncs = true;
  shouldPillsOverflowOnMobile = true;

  successButtons(order: Order): SuccessButtonGroup {
    const giftCardOrderItem: OrderItemAttributes | undefined =
      order.orderItems.find(
        (orderItem) => orderItem.type === OrderItemType.GiftCardOrderItem,
      )?.attributes;

    const orderItemData = giftCardOrderItem?.data as GiftCardOrderItemData;

    const toHomePageButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'wl.westpac.explore_other_deals',
      buttonTheme: 'outline-primary',
      redirectURL: '/',
    };

    // Only fulfilled orders have fulfillmentData attribute
    const downloadMyGiftCardButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'wl.westpac.download_my_gift_card',
      buttonTheme: 'primary',
      redirectURL:
        order.fulfillmentStatus === 'fulfilled' &&
        'fulfillmentUrl' in orderItemData.fulfillmentData[0]
          ? orderItemData.fulfillmentData[0].fulfillmentUrl
          : null,
    };

    const purchaseDifferentGiftCardButton: SuccessButton = {
      buttonClass: 'full-width-on-mobile flips-on-mobile',
      buttonText: 'wl.westpac.purchase_a_different_gift_card',
      buttonTheme: 'primary',
      redirectURL: '/gift_card/listing',
    };

    return {
      confirmed: [toHomePageButton, downloadMyGiftCardButton],
      pending: [{ ...toHomePageButton, buttonTheme: 'primary' }],
      failed: [toHomePageButton, purchaseDifferentGiftCardButton],
    };
  }

  checkoutTermsListItems(params: {
    giftCard: GiftCard;
    selectedDenomination: GiftCardDenomination;
  }): CheckoutTermsListItem[] {
    const { giftCard, selectedDenomination } = params;
    return [
      {
        iconSymbol: 'icon-deliver',
        translationKey: this.giftCardHelper.deliveryMethodInfo(giftCard),
      },
      {
        iconSymbol: 'icon-location-pin',
        translationKey: 'gift_card.checkout.info.redeemable_in_country_only',
        translationParams: { country: 'Australia' },
      },
      {
        iconSymbol: 'icon-validity',
        translationKey:
          this.giftCardHelper.validityDuration(selectedDenomination)
            .translationKey,
        translationParams:
          this.giftCardHelper.validityDuration(selectedDenomination)
            .translationParams,
      },
    ];
  }
}
